import api from 'services/api';
import { array, bool, enforce, nullable, num, object, optional, runSchema, str, unknownValue, withDefault } from 'validation';

import {
  outputMatcherSchema,
  parameterSchema,
  raiseEventSchema,
  retentionPolicySchema,
  waitEventSchema,
  type OutputMatcher,
  type Parameter,
  type RaiseEvent,
  type RetentionPolicy,
  type SaveNodeDefinition,
  type WaitEvent,
} from './types';

// PUT /node_definitions/:zone_name/:name — update. The PUT route handler assembles the response record itself, so
// `UpdatedNodeDefinition` is redeclared in full (equal-by-coincidence with GET-one / POST, not identity — see ../../README.md).
// Structurally identical today; structural typing carries it wherever a `NodeDefinition` is expected. Field invariants are
// documented on `getNodeDefinition`.
export interface UpdatedNodeDefinition {
  readonly node_definition_id: number;
  readonly version: number;
  readonly name: string;
  readonly description: string | null;
  readonly type: string;
  readonly sub_type: string | null;
  readonly documentation: string | null;
  readonly zone_id: number;
  readonly zone_name: string;
  readonly folder_name: string | null;
  readonly default_queue_path: string | null;
  readonly restrict_to_scheduling_window: number | null;
  readonly auto_restart_enabled: boolean;
  readonly auto_restart_amount: number;
  readonly auto_restart_delay_seconds: number;
  readonly restart_on_return_codes: readonly number[];
  readonly hold_on_submit: boolean;
  readonly runtime_limit_min: number | null;
  readonly runtime_limit_max: number | null;
  readonly runtime_limit_min_hhmmss?: string;
  readonly runtime_limit_max_hhmmss?: string;
  readonly runtime_limit_event: number | null;
  readonly runtime_limit_terminate: boolean;
  readonly editable_handles: boolean;
  readonly locks: readonly string[];
  readonly parameters: readonly Parameter[];
  readonly wait_events: readonly WaitEvent[];
  readonly raise_events: readonly RaiseEvent[];
  readonly output_matchers: readonly OutputMatcher[];
  readonly retention_policies: readonly RetentionPolicy[];
  readonly config: unknown;
  readonly handles: readonly unknown[];
  readonly child_nodes: readonly unknown[];
  readonly child_edges: readonly unknown[];
}

const updatedSchema = object<UpdatedNodeDefinition>({
  node_definition_id: num(),
  version: num(),
  name: str(),
  description: nullable(str()),
  type: str(),
  sub_type: nullable(str()),
  documentation: nullable(str()),
  zone_id: num(),
  zone_name: str(),
  folder_name: nullable(str()),
  default_queue_path: nullable(str()),
  restrict_to_scheduling_window: nullable(num()),
  auto_restart_enabled: bool(),
  auto_restart_amount: num(),
  auto_restart_delay_seconds: num(),
  restart_on_return_codes: withDefault(array(num()), []),
  hold_on_submit: bool(),
  runtime_limit_min: nullable(num()),
  runtime_limit_max: nullable(num()),
  runtime_limit_min_hhmmss: optional(str()),
  runtime_limit_max_hhmmss: optional(str()),
  runtime_limit_event: nullable(num()),
  runtime_limit_terminate: bool(),
  editable_handles: bool(),
  locks: withDefault(array(str()), []),
  parameters: withDefault(array(parameterSchema), []),
  wait_events: withDefault(array(waitEventSchema), []),
  raise_events: withDefault(array(raiseEventSchema), []),
  output_matchers: withDefault(array(outputMatcherSchema), []),
  retention_policies: withDefault(array(retentionPolicySchema), []),
  config: unknownValue(),
  handles: withDefault(array(unknownValue()), []),
  child_nodes: withDefault(array(unknownValue()), []),
  child_edges: withDefault(array(unknownValue()), []),
});

/**
 * PUT `/node_definitions/:zone_name/:name`. The URL params identify the *original* (pre-edit) record; the body carries the
 * new values, which may include changes to `name` and `zone_name`. Returns the updated record.
 */
export const putNodeDefinition = async (original: { zone_name: string; name: string }, save: SaveNodeDefinition): Promise<UpdatedNodeDefinition> => {
  const res = await api.put(`/node_definitions/${encodeURIComponent(original.zone_name)}/${encodeURIComponent(original.name)}`, save);
  const result = runSchema(updatedSchema, res.data, 'putNodeDefinition');
  if (!result.ok) {
    throw new Error('putNodeDefinition: node definition response failed validation');
  }
  return enforce(result.value, result.warnings, ['node_definition_id', 'name', 'type', 'zone_name']);
};
